import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Package } from "lucide-react";

// Program Setup — the Signing Agent (or their Agency Admin / Super Admin)
// picks which verticals are being installed at the dealer, then which
// products under each vertical. Submitting flips the onboarding record to
// `received` and hands it off to the internal Production team.
const VSC_PROGRAMS = [
  { key: "new_car", label: "New Car" },
  { key: "preowned", label: "Pre-Owned" },
  { key: "blended", label: "Blended" },
];
export const VSC_PROGRAM_LABELS = Object.fromEntries(VSC_PROGRAMS.map((p) => [p.key, p.label]));

export default function OnboardingProgramSetup() {
  const { id } = useParams();
  const nav = useNavigate();
  const [rec, setRec] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [pickedVerticals, setPickedVerticals] = useState(new Set());
  // productKey -> { product_id, vertical_key, dealer_over_remit, agent_commission }
  const [pickedProducts, setPickedProducts] = useState({});
  const [setupNotes, setSetupNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get(`/onboarding/${id}`),
      api.get("/onboarding/catalog"),
    ]).then(([rRes, cRes]) => {
      setRec(rRes.data);
      setCatalog(cRes.data);
    }).catch((e) => toast.error(e.response?.data?.detail || "Failed to load"));
  }, [id]);

  const productsByVertical = useMemo(() => {
    if (!catalog) return {};
    const byId = Object.fromEntries((catalog.products || []).filter(p => p.active).map(p => [p.id, p]));
    const byVert = {};
    for (const m of catalog.mappings || []) {
      const p = byId[m.product_id];
      if (!p) continue;
      if (!byVert[m.vertical_id]) byVert[m.vertical_id] = [];
      byVert[m.vertical_id].push(p);
    }
    for (const k of Object.keys(byVert)) byVert[k].sort((a, b) => (a.sort_order || 100) - (b.sort_order || 100));
    return byVert;
  }, [catalog]);

  const toggleVertical = (v) => {
    setPickedVerticals((prev) => {
      const next = new Set(prev);
      if (next.has(v.key)) next.delete(v.key); else next.add(v.key);
      return next;
    });
  };

  const toggleProduct = (verticalKey, product) => {
    const k = `${verticalKey}::${product.id}`;
    setPickedProducts((prev) => {
      const next = { ...prev };
      if (next[k]) delete next[k];
      else next[k] = { product_id: product.id, vertical_key: verticalKey, dealer_over_remit: "", agent_commission: "" };
      return next;
    });
  };

  const setProductField = (verticalKey, productId, field, value) => {
    const k = `${verticalKey}::${productId}`;
    setPickedProducts((prev) => ({ ...prev, [k]: { ...(prev[k] || {}), [field]: value } }));
  };

  const submit = async () => {
    const verticals = Array.from(pickedVerticals);
    const products = Object.values(pickedProducts);
    if (!verticals.length) { toast.error("Pick at least one vertical"); return; }
    if (!products.length) { toast.error("Pick at least one product"); return; }
    setSaving(true);
    try {
      await api.post(`/onboarding/${id}/program-setup`, { verticals, products, notes: setupNotes });
      toast.success("Submitted — dealer moved to Received");
      nav("/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally { setSaving(false); }
  };

  if (!rec || !catalog) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="text-sm text-[#6C757D]">Loading…</div>
        </main>
      </div>
    );
  }

  const alreadySubmitted = rec.status !== "pending_program_selection";

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="onboarding-program-setup-page">
        <Link to="/onboarding" className="inline-flex items-center gap-1.5 text-sm text-[#6C757D] hover:text-[#0B132B] mb-4">
          <ArrowLeft size={14}/> Back to onboarding
        </Link>
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">Program Setup</div>
          <h1 className="font-display text-4xl font-black text-[#0B132B] leading-none mt-1">{rec.dealer_name}</h1>
          <div className="text-xs text-[#6C757D] mt-2">Agency: <b>{rec.agency || "—"}</b> · Signing agent: <b>{rec.signing_agent_name || "—"}</b></div>
        </div>

        {alreadySubmitted && (
          <Card className="p-4 bg-green-50 border-green-200 rounded-sm mb-6 flex items-start gap-2 text-sm text-green-800" data-testid="program-setup-locked">
            <CheckCircle2 size={16} className="mt-0.5"/>
            <div>
              This program setup was already submitted — the record is at status <b>{rec.status}</b>. To change products, contact your admin or use the record detail page.
            </div>
          </Card>
        )}

        {!alreadySubmitted && (
          <>
            {/* Verticals */}
            <Card className="p-5 border-[#E9ECEF] rounded-sm mb-4" data-testid="program-setup-verticals-card">
              <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-3">1 · Verticals being installed</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(catalog.verticals || []).filter(v => v.active).map((v) => (
                  <button key={v.id} type="button" onClick={() => toggleVertical(v)}
                    className={`h-11 rounded-sm border text-xs uppercase tracking-[0.14em] font-bold transition-colors ${
                      pickedVerticals.has(v.key)
                        ? "bg-[#0B132B] border-[#0B132B] text-white"
                        : "bg-white border-[#E9ECEF] text-[#0B132B] hover:border-[#0B132B]/40"
                    }`}
                    data-testid={`program-vertical-${v.key}`}>
                    {v.name}
                  </button>
                ))}
              </div>
            </Card>

            {/* Products per selected vertical */}
            {(catalog.verticals || []).filter(v => pickedVerticals.has(v.key)).map((v) => {
              const products = productsByVertical[v.id] || [];
              return (
                <Card key={v.id} className="p-5 border-[#E9ECEF] rounded-sm mb-4" data-testid={`program-vertical-products-${v.key}`}>
                  <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-3 flex items-center gap-2">
                    <Package size={14} className="text-[#D4AF37]"/> {v.name}
                  </div>
                  {products.length === 0 ? (
                    <div className="text-xs text-[#6C757D] italic">No products mapped to this vertical yet — a super-admin can add them from Admin → Products.</div>
                  ) : (
                    <>
                      <div className="hidden md:grid grid-cols-[1fr,120px,120px] gap-3 pb-2 border-b border-[#F1F3F5] text-[9px] uppercase tracking-[0.16em] font-bold text-[#6C757D]">
                        <div>Product</div>
                        <div className="text-right">Dealer OverRemit</div>
                        <div className="text-right">Agent Commission</div>
                      </div>
                      <div className="space-y-2 mt-2">
                        {products.map((p) => {
                          const k = `${v.key}::${p.id}`;
                          const picked = !!pickedProducts[k];
                          return (
                            <div key={p.id} className={`border rounded-sm ${picked ? "border-[#D4AF37] bg-[#D4AF37]/8" : "border-[#E9ECEF] bg-white"}`}>
                              <div className="grid grid-cols-[1fr,120px,120px] gap-3 items-center px-3 py-2.5">
                                <label className="flex items-center gap-3 cursor-pointer min-w-0">
                                  <input type="checkbox" checked={picked} onChange={() => toggleProduct(v.key, p)}
                                    className="w-4 h-4" data-testid={`program-product-${v.key}-${p.key}`} />
                                  <div className="min-w-0">
                                    <div className="text-sm font-bold text-[#0B132B] truncate">{p.name}</div>
                                    {p.description && <div className="text-[11px] text-[#6C757D] truncate">{p.description}</div>}
                                  </div>
                                </label>
                                <Input
                                  type="text" inputMode="decimal"
                                  value={pickedProducts[k]?.dealer_over_remit || ""}
                                  onChange={(e) => setProductField(v.key, p.id, "dealer_over_remit", e.target.value)}
                                  placeholder="$0"
                                  disabled={!picked}
                                  className="h-9 rounded-sm text-sm text-right"
                                  data-testid={`program-product-overremit-${v.key}-${p.key}`}
                                />
                                <Input
                                  type="text" inputMode="decimal"
                                  value={pickedProducts[k]?.agent_commission || ""}
                                  onChange={(e) => setProductField(v.key, p.id, "agent_commission", e.target.value)}
                                  placeholder="$0"
                                  disabled={!picked}
                                  className="h-9 rounded-sm text-sm text-right"
                                  data-testid={`program-product-commission-${v.key}-${p.key}`}
                                />
                              </div>
                              {picked && v.key === "automotive" && /vsc/i.test(p.name || p.key || "") && !/subscription/i.test(p.name || p.key || "") && (
                                <div className="px-3 pb-2.5 -mt-1 flex flex-wrap items-center gap-3" data-testid={`vsc-programs-${p.key}`}>
                                  <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D]">Which VSC program(s) to install:</span>
                                  {VSC_PROGRAMS.map((opt) => {
                                    const on = (pickedProducts[k]?.vsc_programs || []).includes(opt.key);
                                    return (
                                      <label key={opt.key} className={`flex items-center gap-1.5 text-xs cursor-pointer px-2 py-1 rounded-sm border ${on ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-[#E9ECEF] bg-white text-[#0B132B]"}`}>
                                        <input type="checkbox" checked={on} className="w-3.5 h-3.5 accent-[#D4AF37]"
                                          onChange={() => {
                                            const cur = pickedProducts[k]?.vsc_programs || [];
                                            setProductField(v.key, p.id, "vsc_programs", on ? cur.filter((x) => x !== opt.key) : [...cur, opt.key]);
                                          }}
                                          data-testid={`vsc-program-${opt.key}`} />
                                        {opt.label}
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </Card>
              );
            })}

            {/* Single bottom notes block — replaces the per-row Setup Notes */}
            {Object.values(pickedProducts).some(Boolean) && (
              <Card className="p-5 border-[#E9ECEF] rounded-sm mb-4" data-testid="program-setup-notes-card">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Setup notes (optional)</Label>
                <textarea
                  value={setupNotes}
                  onChange={(e) => setSetupNotes(e.target.value)}
                  rows={4}
                  placeholder="Anything the internal Production team should know about this dealer's setup — special product configs, timing, contacts…"
                  className="mt-2 w-full rounded-sm border border-[#E9ECEF] px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]"
                  data-testid="program-setup-notes"
                />
              </Card>
            )}

            <div className="flex items-center justify-end gap-3 sticky bottom-4 bg-[#F8F9FA] pt-3">
              <Button variant="outline" onClick={() => nav("/onboarding")} className="h-11 rounded-sm">Cancel</Button>
              <Button onClick={submit} disabled={saving} className="h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold"
                data-testid="program-setup-submit">
                {saving ? "Submitting…" : "Submit and move to Received"}
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
